import { type SxProps, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

export const LIVE_TRANSCRIPT_COPY_BUTTON_CLASS = 'live-transcript-copy-button';

const createCenteredSquareSx = (sizeRem: number): SxProps<Theme> => ({
  width: `${sizeRem}rem`,
  height: `${sizeRem}rem`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const createCenteredIconSx = (sizeRem: number): SxProps<Theme> => ({
  ...createCenteredSquareSx(sizeRem),
  fontSize: `${sizeRem}rem`,
  lineHeight: 1,
});

export const liveTranscriptDrawerContainerSx = (
  isCollapsed: boolean,
  heightPx: number,
  isResizing: boolean,
): SxProps<Theme> => ({
  position: 'absolute',
  left: '0.5rem',
  right: '0.5rem',
  bottom: '0.5rem',
  height: isCollapsed ? '3rem' : `${heightPx}px`,
  zIndex: 5,
  borderRadius: 2,
  border: (theme) => `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  backgroundColor: (theme) => alpha(theme.palette.custom.OV.sys.surfaceContainer, 0.35),
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  boxShadow: (theme) => theme.shadows[1],
  overflow: 'hidden',
  transition: isResizing ? 'none' : 'height 180ms ease-in-out',
  userSelect: isResizing ? 'none' : 'auto',
});

export const liveTranscriptResizeHandleSx: SxProps<Theme> = {
  height: '0.875rem',
  cursor: 'ns-resize',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none',
  touchAction: 'none',
  '&::before': {
    content: '""',
    width: '2.5rem',
    height: '0.25rem',
    borderRadius: 2,
    backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.25),
  },
};

export const liveTranscriptHeaderSx = (isCollapsed: boolean): SxProps<Theme> => ({
  px: 1.5,
  py: 1,
  minHeight: '3rem',
  cursor: isCollapsed ? 'pointer' : 'default',
  borderBottom: isCollapsed ? 'none' : (theme) => `1px solid ${theme.palette.divider}`,
});

export const liveTranscriptHeaderStatusRowSx: SxProps<Theme> = {
  alignItems: 'center',
  gap: 1,
};

export const liveTranscriptHeaderActionsSx: SxProps<Theme> = {
  alignItems: 'center',
  gap: 0.5,
};

export const liveTranscriptHeaderIconButtonSx: SxProps<Theme> = {
  p: 0,
  ...createCenteredSquareSx(1.75),
};

export const liveTranscriptHeaderCopyIconSx: SxProps<Theme> = createCenteredIconSx(1);

export const liveTranscriptHeaderToggleIconSx: SxProps<Theme> = createCenteredIconSx(1.15);

export const liveTranscriptMessageContainerSx = (isAgent: boolean): SxProps<Theme> => ({
  alignSelf: isAgent ? 'flex-end' : 'flex-start',
  width: '100%',
  alignItems: isAgent ? 'flex-end' : 'flex-start',
  gap: 0.25,
  animation: 'messageIn 180ms ease-out',
  '@keyframes messageIn': {
    from: { opacity: 0, transform: 'translateY(0.375rem)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
});

export const liveTranscriptSpeakerChangeSx: SxProps<Theme> = {
  px: 0.5,
};

export const liveTranscriptMessageRowSx: SxProps<Theme> = {
  gap: 0.5,
  alignItems: 'center',
  maxWidth: '100%',
  [`& .${LIVE_TRANSCRIPT_COPY_BUTTON_CLASS}`]: {
    opacity: 0,
    pointerEvents: 'none',
  },
  [`&:hover .${LIVE_TRANSCRIPT_COPY_BUTTON_CLASS}, &:focus-within .${LIVE_TRANSCRIPT_COPY_BUTTON_CLASS}`]:
    {
      opacity: 1,
      pointerEvents: 'auto',
    },
};

export const liveTranscriptMessageBubbleSx = (isAgent: boolean): SxProps<Theme> => ({
  px: 1,
  py: 0.75,
  borderRadius: 1.5,
  backgroundColor: (theme) =>
    isAgent ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.08),
  color: (theme) => (isAgent ? theme.palette.primary.contrastText : theme.palette.text.primary),
  maxWidth: '100%',
});

export const liveTranscriptCopyButtonSx: SxProps<Theme> = {
  transition: 'opacity 120ms ease-in-out',
  p: 0,
  ...createCenteredSquareSx(1.5),
};

export const liveTranscriptCopyIconSx: SxProps<Theme> = createCenteredIconSx(0.95);

export const liveTranscriptSearchBarSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1.5,
  height: '3rem',
  minHeight: '3rem',
  flexShrink: 0,
  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
};

export const liveTranscriptSearchInputWrapSx: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  px: 1,
  height: '1.875rem',
  borderRadius: 1,
  border: (theme) => `1px solid ${theme.palette.divider}`,
  backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
  minWidth: 0,
};

export const liveTranscriptSearchInputSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  fontSize: '0.875rem',
  '& .MuiInputBase-input': {
    p: 0,
  },
};

export const liveTranscriptSearchIconSx: SxProps<Theme> = {
  ...createCenteredIconSx(0.875),
  color: (theme) => theme.palette.text.secondary,
};

export const liveTranscriptSearchCountSx = (hasResults: boolean): SxProps<Theme> => ({
  fontSize: '0.75rem',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  color: (theme) =>
    hasResults ? theme.palette.text.secondary : theme.palette.error.main,
});

export const liveTranscriptHighlightSx = (isCurrent: boolean): SxProps<Theme> => ({
  backgroundColor: (theme) =>
    isCurrent ? theme.palette.warning.main : alpha(theme.palette.warning.main, 0.35),
  color: (theme) =>
    isCurrent ? theme.palette.warning.contrastText : 'inherit',
  borderRadius: '0.1875rem',
  px: '0.0625rem',
  mx: '-0.0625rem',
  boxShadow: (theme) =>
    isCurrent ? `0 0 0 1px ${theme.palette.warning.main}` : 'none',
});

export const liveTranscriptMessagesContainerSx: SxProps<Theme> = {
  px: 1.5,
  py: 1,
  gap: 1,
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
};

export const liveTranscriptStreamingIndicatorSx: SxProps<Theme> = {
  alignSelf: 'flex-start',
  px: 1,
  py: 0.75,
  alignItems: 'center',
  gap: 0.5,
  borderRadius: 1.5,
  backgroundColor: (theme) => theme.palette.action.hover,
  border: (theme) => `1px solid ${theme.palette.divider}`,
};

export const liveTranscriptStreamingDotSx = (dot: number): SxProps<Theme> => ({
  width: '0.375rem',
  height: '0.375rem',
  borderRadius: '999px',
  backgroundColor: 'text.secondary',
  opacity: 0.45,
  animation: 'transcriptionDot 900ms ease-in-out infinite',
  animationDelay: `${dot * 120}ms`,
  '@keyframes transcriptionDot': {
    '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.45 },
    '40%': { transform: 'translateY(-0.1875rem)', opacity: 1 },
  },
});

export const liveTranscriptWaveformRootSx: SxProps<Theme> = {
  alignItems: 'center',
  gap: 0.4,
  height: '1rem',
  '@keyframes liveWave1': {
    '0%': { height: '0.5rem', opacity: 0.8 },
    '25%': { height: '0.875rem', opacity: 1 },
    '50%': { height: '0.375rem', opacity: 0.7 },
    '75%': { height: '0.75rem', opacity: 0.9 },
    '100%': { height: '0.5rem', opacity: 0.8 },
  },
  '@keyframes liveWave2': {
    '0%': { height: '0.875rem', opacity: 1 },
    '25%': { height: '0.5rem', opacity: 0.8 },
    '50%': { height: '1rem', opacity: 1 },
    '75%': { height: '0.625rem', opacity: 0.85 },
    '100%': { height: '0.875rem', opacity: 1 },
  },
  '@keyframes liveWave3': {
    '0%': { height: '0.625rem', opacity: 0.85 },
    '25%': { height: '1rem', opacity: 1 },
    '50%': { height: '0.5rem', opacity: 0.75 },
    '75%': { height: '0.875rem', opacity: 0.95 },
    '100%': { height: '0.625rem', opacity: 0.85 },
  },
  '@keyframes liveWave4': {
    '0%': { height: '0.75rem', opacity: 0.9 },
    '25%': { height: '0.375rem', opacity: 0.7 },
    '50%': { height: '0.875rem', opacity: 1 },
    '75%': { height: '0.5rem', opacity: 0.8 },
    '100%': { height: '0.75rem', opacity: 0.9 },
  },
};

export const liveTranscriptWaveBarSx = ({
  isFinished,
  isStreaming,
  staticHeight,
  animationName,
  delay,
}: {
  isFinished: boolean;
  isStreaming: boolean;
  staticHeight: string;
  animationName: string;
  delay: string;
}): SxProps<Theme> => ({
  width: '0.1875rem',
  height: isFinished ? staticHeight : undefined,
  borderRadius: '0.125rem',
  backgroundColor: isFinished ? 'text.secondary' : 'success.main',
  opacity: isFinished ? 0.4 : 0.9,
  animation: isStreaming && !isFinished ? `${animationName} 1.6s ease-in-out infinite` : 'none',
  animationDelay: delay,
});
